import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import {
  getPatientNotes,
  getPatientCareplanNotes,
} from "../../../services/patient/details";
import {
  getLayerZIndex,
  getOverlayRoot,
} from "../../../utils/layerManager";
import { Checkbox } from "../../ui/checkbox";
import NotesControls from "./controls";
import NotesList from "./list";
import styles from "./NotesDrawer.module.css";

/**
 * NotesDrawer — expanded full-height view of the Notes panel.
 *
 * Slides in from the right edge of the viewport over a dimmed backdrop.
 * Runs its OWN sort + program-filter state so opening/closing the drawer
 * never affects the inline panel and vice versa. Reuses <NotesList> for
 * row rendering (timeline dots, kebab menu, Edit/Delete dialogs) and
 * <NotesControls> for the sort dropdown — no JSX duplication.
 *
 * Rendered via portal at #overlay-root so it escapes the .notesAiWrap
 * 820px cap on the right-rail panel. Backdrop click and Esc close the
 * drawer; the parent restores focus to the expand trigger.
 */
const NotesDrawer = ({ isOpen, onClose, chatBotType, onNoteClick }) => {
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const patientId = sessionStorage.getItem("_pt_id");

  const [sort, setSort] = useState("latest");
  const [rpmOn, setRpmOn] = useState(true);
  const [ccmOn, setCcmOn] = useState(true);
  const closeBtnRef = useRef(null);

  const {
    data: notes,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["patient-notes", patientId, sort],
    () => getPatientNotes(null, sort, patientId, locationId),
    {
      enabled: !!patientId && isOpen,
      staleTime: 2 * 60 * 1000,
    }
  );

  const { data: careplanNotes } = useQuery(
    ["patient-careplan-notes", patientId],
    () => getPatientCareplanNotes(patientId, locationId),
    {
      enabled: !!patientId && isOpen,
      select: (data) =>
        data.map((note) => ({
          ...note,
          note_type: "careplan_note",
          note_text: note.note,
          // Careplan notes carry no created_at; updated_at is the
          // finalize timestamp. Aliasing it keeps the row date and the
          // merge-sort consistent with RPM/CCM notes.
          created_at: note.created_at ?? note.updated_at,
          assigned_by_name: note.finalized_by_name,
        })),
    }
  );

  const items = useMemo(() => {
    const merged = [...(notes || []), ...(careplanNotes || [])];
    merged.sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return sort === "oldest" ? ta - tb : tb - ta;
    });
    return merged;
  }, [notes, careplanNotes, sort]);

  // Body scroll lock + Esc handler + initial focus.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  // Reset internal state when the drawer closes so the next open starts
  // from the default Latest + both-checked configuration.
  useEffect(() => {
    if (!isOpen) {
      setSort("latest");
      setRpmOn(true);
      setCcmOn(true);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const z = getLayerZIndex("drawer");

  return createPortal(
    <>
      <div
        className={styles.backdrop}
        style={{ zIndex: z }}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={styles.surface}
        style={{ zIndex: z + 1 }}
        role="dialog"
        aria-modal="true"
        aria-label="Notes — expanded view"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <button
            type="button"
            ref={closeBtnRef}
            className={styles.collapseBtn}
            onClick={onClose}
            aria-label="Collapse Notes drawer"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <h2 className={styles.title}>All Notes</h2>

          <label className={styles.filterRow}>
            <Checkbox checked={rpmOn} onCheckedChange={setRpmOn} />
            <span className={styles.filterLabel}>RPM</span>
          </label>
          <label className={styles.filterRow}>
            <Checkbox checked={ccmOn} onCheckedChange={setCcmOn} />
            <span className={styles.filterLabel}>CCM</span>
          </label>

          <div className={styles.spacer} />

          <NotesControls defaultSort={sort} handleSort={setSort} />
        </div>

        <div className={styles.body}>
          <NotesList
            notes={items}
            isLoading={isLoading}
            error={error}
            refetch={refetch}
            onNoteClick={onNoteClick}
            chatBotType={chatBotType}
            programFilter={{ rpm: rpmOn, ccm: ccmOn }}
          />
        </div>
      </aside>
    </>,
    getOverlayRoot()
  );
};

export default NotesDrawer;
